import { memo, useRef } from 'react';
import { TorchButton } from '../../shared-camera/components/TorchButton';
import type { DecodedSymbol, DecodedSymbolKind } from '../types';
import { FpsHud } from './FpsHud';

// Color palette per detector kind. Each bbox is rendered atop a 2 px
// black underlay (in the symbol map below) for ≥3:1 contrast against
// arbitrary video content.
function colorForKind(kind: DecodedSymbolKind): string {
  switch (kind) {
    case 'apriltag':
      return '#14b8a6'; // teal
    case 'aruco':
      return '#d946ef'; // magenta
    case 'text':
      return '#f59e0b'; // amber
    case 'qr':
    default:
      return 'rgb(16 185 129)'; // emerald (unchanged from pre-fiducial)
  }
}

// QR text can be a URL or arbitrary string — truncate so a long bin URL
// doesn't run off the frame. Fiducial text is already the integer ID.
const QR_LABEL_MAX = 16;
function labelForSymbol(sym: DecodedSymbol): string {
  if (sym.kind === 'qr' && sym.text.length > QR_LABEL_MAX) {
    return sym.text.slice(0, QR_LABEL_MAX) + '…';
  }
  return sym.text;
}

interface LivePreviewProps {
  videoRef: React.MutableRefObject<HTMLVideoElement | null>;
  /** The last batch of decoded symbols, in source-image (post-downscale) coords. */
  lastDecodedSymbols: DecodedSymbol[];
  /** Source image dimensions the bboxes are in — used to translate to SVG viewport. */
  decodeFrameSize: { width: number; height: number } | null;
  /**
   * Count of QRs dispatched together in the most recent multi-symbol frame.
   * Parent sets when prepared.length >= 2 and auto-clears after a short
   * window. Drives the "Captured N" chip + bbox pulse so the operator sees
   * that the dispatch was parallel, not sequential.
   */
  burstCount?: number;
  /** Optional torch controls — rendered as an overlay when supported. */
  torchSupported?: boolean;
  torchOn?: boolean;
  onToggleTorch?: () => void;
  /** Camera-source FPS from the video track (rVFC), or null when unavailable. */
  cameraFps?: number | null;
  /** Rate of completed decodeActiveFormat calls, or null when idle. */
  processedFps?: number | null;
}

/**
 * Live camera + an SVG overlay that anchors a green check to each
 * currently-decoded QR. Overlay coords come from zxing's bbox in the
 * downscaled-frame coordinate space; we project them into the video's
 * displayed dimensions via SVG's viewBox.
 *
 * The overlay flickers naturally as the frame sampler cycles — that's
 * intended feedback. We don't smooth across frames; the operator should
 * see when detection drops.
 */
function LivePreviewInner({
  videoRef,
  lastDecodedSymbols,
  decodeFrameSize,
  burstCount = 0,
  torchSupported = false,
  torchOn = false,
  onToggleTorch,
  cameraFps = null,
  processedFps = null,
}: LivePreviewProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const viewBox = decodeFrameSize
    ? `0 0 ${decodeFrameSize.width} ${decodeFrameSize.height}`
    : '0 0 1024 768';

  return (
    <div
      ref={containerRef}
      className="relative aspect-[4/3] w-full overflow-hidden rounded-lg bg-black ring-1 ring-gray-700"
      data-testid="live-preview"
    >
      <video
        ref={videoRef}
        className="h-full w-full object-cover"
        autoPlay
        muted
        playsInline
        aria-label="Continuous-scan camera preview"
      />

      {lastDecodedSymbols.length > 0 && (
        <svg
          className={[
            'pointer-events-none absolute inset-0 h-full w-full',
            burstCount >= 2 ? 'animate-pulse' : '',
          ].join(' ')}
          viewBox={viewBox}
          preserveAspectRatio="xMidYMid slice"
          aria-hidden="true"
        >
          {lastDecodedSymbols.map((sym, idx) => {
            const { x1, y1, x2, y2 } = sym.bbox;
            const w = Math.max(1, x2 - x1);
            const h = Math.max(1, y2 - y1);
            const color = colorForKind(sym.kind);
            const strokeWidth = Math.max(2, w * 0.02);
            const label = labelForSymbol(sym);
            // viewBox is the decode raster (e.g. ~1600px wide), so font
            // metrics scale with bbox size — small bboxes get small text,
            // large bboxes get prominent text. Floor keeps far-distance
            // labels still readable.
            const fontSize = Math.max(28, w * 0.18);
            const labelX = (x1 + x2) / 2;
            const labelY = Math.max(fontSize + 4, y1 - 4);
            return (
              <g key={`${sym.text}-${idx}`}>
                {/* Black underlay rect — 2px wider stroke for ≥3:1 contrast against arbitrary video. */}
                <rect
                  x={x1}
                  y={y1}
                  width={w}
                  height={h}
                  fill="none"
                  stroke="#000"
                  strokeWidth={strokeWidth + 2}
                />
                <rect
                  x={x1}
                  y={y1}
                  width={w}
                  height={h}
                  fill="none"
                  stroke={color}
                  strokeWidth={strokeWidth}
                />
                <circle
                  cx={x2 - w * 0.12}
                  cy={y1 + h * 0.12}
                  r={Math.max(8, w * 0.08)}
                  fill={color}
                  stroke="#000"
                  strokeWidth={2}
                />
                {/* Label with paint-order outline so text reads against any background. */}
                <text
                  x={labelX}
                  y={labelY}
                  fill="#fff"
                  stroke="#000"
                  strokeWidth={Math.max(4, fontSize * 0.18)}
                  paintOrder="stroke"
                  textAnchor="middle"
                  fontSize={fontSize}
                  fontWeight={600}
                  style={{ fontFamily: 'ui-sans-serif, system-ui, sans-serif' }}
                >
                  {label}
                </text>
              </g>
            );
          })}
        </svg>
      )}

      <FpsHud cameraFps={cameraFps} processedFps={processedFps} />

      {torchSupported && onToggleTorch && (
        <TorchButton on={torchOn} onToggle={onToggleTorch} />
      )}

      {burstCount >= 2 && (
        <div
          className="pointer-events-none absolute left-1/2 top-3 -translate-x-1/2 rounded-full bg-emerald-500/90 px-3 py-1 text-xs font-semibold text-white shadow-lg ring-1 ring-emerald-300/40"
          role="status"
          data-testid="burst-chip"
        >
          Captured {burstCount} at once
        </div>
      )}
    </div>
  );
}

export const LivePreview = memo(LivePreviewInner);
